/**
 * KCKBCK size sheet: on phones, the size picker that slides up from the bottom when the round cart
 * button of a product card is tapped. Markup and styles: snippets/kck-size-sheet.liquid.
 *
 * There is a single sheet per page. It is filled, each time it opens, with the data of the tapped
 * card (see snippets/kck-quick-add.liquid): product title, photo, price, link and one chip per
 * size, in the order the card lists them.
 *
 * No size is preselected, so nobody orders a size they did not pick. The button always reads
 * "Añadir al carrito · price"; tapping it before choosing a size highlights the sizes instead of
 * adding. Adding closes the sheet and opens the cart panel (assets/kck-mini-cart.js).
 *
 * On wider screens this file does nothing and the card shows its own small picker.
 */

const PHONE_QUERY = '(max-width: 749px)';
const CART_UPDATE_EVENT = 'kckbck:cart-update';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';

class KckSizeSheet extends HTMLElement {
  /** The card (kck-quick-add) the sheet was opened from. @type {HTMLElement | null} */
  #card = null;
  /** @type {HTMLButtonElement | null} */
  #selected = null;
  /** @type {HTMLElement | null} */
  #returnFocusTo = null;
  /** @type {string | null} */
  #previousOverflow = null;

  connectedCallback() {
    if (this.kckReady) return;
    this.kckReady = true;

    this.panel = /** @type {HTMLElement | null} */ (this.querySelector('[data-kck-sheet-panel]'));
    if (!this.panel) return;

    // Capture phase: on phones the tap is taken here, before the card opens its own picker.
    document.addEventListener('click', this.#onCardButtonClick, true);
    document.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('click', this.#onClick);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * @param {string} name
   * @returns {HTMLElement | null}
   */
  #part(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-kck-sheet-${name}]`));
  }

  /** @param {HTMLElement} card */
  show(card) {
    if (!this.panel) return;

    this.#card = card;
    this.#selected = null;
    this.removeAttribute('needs-size');

    const title = this.#part('title');
    if (title) title.textContent = card.dataset.productTitle || '';

    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-kck-sheet-image]'));
    if (image) {
      image.hidden = !card.dataset.productImage;
      if (card.dataset.productImage) image.src = card.dataset.productImage;
    }

    const details = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-kck-sheet-details]'));
    if (details) details.href = card.dataset.productUrl || '#';

    // One chip per size, copied from the card's own (hidden) size buttons, in the same order.
    const grid = this.#part('grid');
    if (grid) {
      const chips = Array.from(card.querySelectorAll('.kck-quick-add__size')).map((source) => {
        const size = /** @type {HTMLButtonElement} */ (source);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'kck-sheet__size';
        chip.textContent = size.textContent?.trim() || '';
        chip.dataset.variantId = size.dataset.variantId || '';
        chip.dataset.price = size.dataset.price || '';
        chip.disabled = size.disabled;
        chip.setAttribute('aria-pressed', 'false');
        if (size.disabled) chip.setAttribute('aria-label', `${chip.textContent} (agotada)`);
        return chip;
      });
      grid.replaceChildren(...chips);
    }

    this.#showMessage('');
    this.#render();

    const active = document.activeElement;
    this.#returnFocusTo = active instanceof HTMLElement && active !== document.body ? active : null;

    if (this.#previousOverflow === null) {
      this.#previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }

    this.panel.inert = false;
    this.setAttribute('open', '');
    this.panel.focus({ preventScroll: true });
  }

  hide() {
    if (!this.panel || !this.isOpen) return;

    this.removeAttribute('open');
    this.panel.inert = true;

    if (this.#previousOverflow !== null) {
      document.documentElement.style.overflow = this.#previousOverflow;
      this.#previousOverflow = null;
    }

    if (this.#returnFocusTo?.isConnected) this.#returnFocusTo.focus({ preventScroll: true });
    this.#returnFocusTo = null;
  }

  /** Updates the "Talla X · price" line and the button from the selected size. */
  #render() {
    const card = this.#card;
    const selected = this.#selected;
    const subtitle = this.#part('subtitle');
    const add = this.#part('add');

    this.querySelectorAll('.kck-sheet__size').forEach((chip) => {
      chip.setAttribute('aria-pressed', chip === selected ? 'true' : 'false');
    });

    const price = selected?.dataset.price || card?.dataset.productPrice || '';

    if (subtitle) subtitle.textContent = selected ? `Talla ${selected.textContent} · ${price}` : price;
    if (add) add.textContent = `${add.dataset.label || ''} · ${price}`;
  }

  async #add() {
    const selected = this.#selected;
    const card = this.#card;
    const add = this.#part('add');
    if (!card || this.hasAttribute('busy')) return;

    if (!selected?.dataset.variantId) {
      // No size yet: point at the sizes instead of adding anything.
      this.setAttribute('needs-size', '');
      this.#showMessage(add?.dataset.missingSize || '');
      return;
    }

    this.setAttribute('busy', '');
    this.#showMessage('');

    const addUrl = card.dataset.addUrl || '/cart/add';
    const cartUrl = card.dataset.cartUrl || '/cart';

    try {
      const response = await fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: Number(selected.dataset.variantId), quantity: 1 }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.description || data?.message || '');

      const cart = await fetch(`${cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store' })
        .then((cartResponse) => cartResponse.json())
        .catch(() => null);

      if (cart) {
        document.querySelectorAll('cart-icon').forEach((icon) => {
          const cartIcon = /** @type {any} */ (icon);
          if (typeof cartIcon.renderCartBubble === 'function') cartIcon.renderCartBubble(cart.item_count);
        });
      }

      const variantId = selected.dataset.variantId;
      this.#returnFocusTo = null;
      this.hide();

      // The cart panel listens to this and opens with the new item.
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, { bubbles: true, detail: { cart, addedVariantId: variantId } })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      this.#showMessage(message || card.dataset.errorMessage || '');
    } finally {
      this.removeAttribute('busy');
    }
  }

  /** @param {string} text */
  #showMessage(text) {
    const message = this.#part('message');
    if (!message) return;
    message.textContent = text;
    message.hidden = text === '';
  }

  /** @param {MouseEvent} event */
  #onCardButtonClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target?.closest('[data-kck-qa-toggle]');
    // A button that carries a variant id belongs to a product without options: the card adds it directly.
    if (!toggle || toggle.hasAttribute('data-variant-id')) return;
    if (!window.matchMedia(PHONE_QUERY).matches) return;

    const card = toggle.closest('kck-quick-add');
    if (!(card instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.show(card);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-kck-sheet-close]')) {
      this.hide();
      return;
    }

    const chip = target.closest('.kck-sheet__size');
    if (chip instanceof HTMLButtonElement) {
      if (chip.disabled) return;
      this.#selected = chip;
      this.removeAttribute('needs-size');
      this.#showMessage('');
      this.#render();
      return;
    }

    if (target.closest('[data-kck-sheet-add]')) this.#add();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.isOpen || !this.panel) return;

    if (event.key === 'Escape') {
      this.hide();
      return;
    }

    // Keep Tab inside the sheet while it is open.
    if (event.key !== 'Tab') return;
    const focusable = /** @type {HTMLElement[]} */ (Array.from(this.panel.querySelectorAll(FOCUSABLE_SELECTOR)));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === this.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get('kck-size-sheet')) {
  customElements.define('kck-size-sheet', KckSizeSheet);
}
